import { APP_ID, executePrivateGraphQl, getCurrentAppId, graphQlUrl, privateGraphQlUrl, publicGraphQlUrl, resolveGraphQlMeta } from '../utilities';
import axiosInstance from '../axiosInstance';
import { normalizePracticeAreaAppId } from '@lib/juristHome/practiceAreas';

const SERIESAI_APP_IDS = new Set(['3', '4']);

const sameAppId = (left, right) => {
  if (SERIESAI_APP_IDS.has(String(left).trim()) && SERIESAI_APP_IDS.has(String(right).trim())) {
    return true;
  }

  const normalizedLeft = normalizePracticeAreaAppId(left);
  const normalizedRight = normalizePracticeAreaAppId(right);

  if (normalizedLeft && normalizedRight) {
    return normalizedLeft === normalizedRight;
  }

  return String(left) === String(right);
};

const USER_LOOKUP_CACHE = new Map();
const USER_ID_GRAPHQL_SCALAR = graphQlUrl === publicGraphQlUrl ? 'ID!' : 'String!';
const getGraphQlErrors = (response) => Array.isArray(response?.data?.errors) ? response.data.errors : Array.isArray(response?.errors) ? response.errors : [];
const USER_PROFILE_FIELDS = [
  'assignedCases', 'userId', 'organizationId', 'organizationAccessLevel', 'accountType', 'username',
  'email', 'firstName', 'lastName', 'profileImage', 'createdDate', 'legalTeamIds', 'notificationCadence',
  'digestTime', 'caseNotificationPrefs', 'notificationCaps', 'quietHours', 'timezone', 'firmDigestReport',
  'phoneNumber', 'firmName', 'firmAddress', 'barNumber', 'barAdmissions', 'updateChannel',
];
const REQUIRED_USER_PROFILE_FIELDS = new Set(['userId', 'email']);

const parseGraphQlErrorBody = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getMissingUserProfileFields = (response) => {
  let payload = response?.response?.data ?? response?.data ?? response;
  payload = parseGraphQlErrorBody(payload);
  if (payload && Object.prototype.hasOwnProperty.call(payload, 'body')) {
    payload = parseGraphQlErrorBody(payload.body);
  }

  const errors = Array.isArray(payload?.errors)
    ? payload.errors
    : Array.isArray(payload?.data?.errors)
      ? payload.data.errors
      : [];
  const missing = new Set();
  errors.forEach(({ message = '' } = {}) => {
    const match = String(message).match(/Cannot query field\s+['\"]([^'\"]+)['\"]\s+on type\s+['\"]?UserType\b/i);
    if (match?.[1]) missing.add(match[1]);
  });
  return [...missing];
};

const buildUserProfileQuery = (excludedFields = new Set()) => `
  query MyQuery($email: String!) {
    user(email: $email) {
      ${USER_PROFILE_FIELDS.filter((field) => !excludedFields.has(field)).join('\n      ')}
    }
  }
`;

const normalizeString = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeUserProfile = (user) => {
  if (!user || typeof user !== 'object') {
    return user;
  }

  const normalized = { ...user };
  const userId =
    normalized.userId ??
    normalized.user_id ??
    normalized.cognito_user_id ??
    normalized.cognitoUserId ??
    normalized.id;
  const firstName = normalized.firstName ?? normalized.first_name;
  const lastName = normalized.lastName ?? normalized.last_name;
  const profileImage =
    normalized.profileImage ??
    normalized.profile_image ??
    normalized.profilePicture ??
    normalized.profile_picture ??
    normalized.avatar ??
    normalized.avatarUrl ??
    normalized.avatar_url ??
    normalized.photo ??
    normalized.photoUrl ??
    normalized.image ??
    normalized.imageUrl ??
    '';
  const legalTeamIds = normalizeArray(normalized.legalTeamIds ?? normalized.legal_team_ids);
  const assignedCases = normalizeArray(normalized.assignedCases ?? normalized.assigned_cases);

  return {
    ...normalized,
    id: normalized.id ?? userId,
    userId,
    cognito_user_id: normalized.cognito_user_id ?? userId,
    email: normalizeString(normalized.email) || normalized.email,
    firstName,
    first_name: normalized.first_name ?? firstName,
    lastName,
    last_name: normalized.last_name ?? lastName,
    name:
      normalized.name ??
      [firstName, lastName]
        .filter(Boolean)
        .join(' ')
        .trim(),
    profileImage,
    profile_image: normalized.profile_image ?? profileImage,
    profilePicture: normalized.profilePicture ?? profileImage,
    profile_picture: normalized.profile_picture ?? profileImage,
    avatar: normalized.avatar ?? profileImage,
    avatarUrl: normalized.avatarUrl ?? profileImage,
    avatar_url: normalized.avatar_url ?? profileImage,
    photo: normalized.photo ?? profileImage,
    photoUrl: normalized.photoUrl ?? profileImage,
    legalTeamIds,
    assignedCases,
  };
};

const normalizeUserArgs = (input) => {
  const contextMeta = resolveGraphQlMeta();

  const base = {
    email: undefined,
    userId: contextMeta.userId,
    appId: contextMeta.appId || APP_ID,
  };

  if (!input) {
    return base;
  }

  if (typeof input === 'string') {
    return { ...base, email: input };
  }

  const { email, userId, appId } = input;

  return {
    email,
    userId: userId ?? base.userId,
    appId: appId ?? base.appId ?? APP_ID,
  };
};

const buildMetaPayload = ({ userId, appId } = {}) => resolveGraphQlMeta({ userId, appId });

const cacheUserProfile = (user) => {
  const normalizedUser = normalizeUserProfile(user);
  const userIdKey = normalizeString(normalizedUser?.userId);
  const emailKey = normalizeString(normalizedUser?.email).toLowerCase();

  if (userIdKey) {
    USER_LOOKUP_CACHE.set(`userId:${userIdKey}`, normalizedUser);
  }

  if (emailKey) {
    USER_LOOKUP_CACHE.set(`email:${emailKey}`, normalizedUser);
  }

  return normalizedUser;
};

const readCachedUserProfile = ({ userId, email } = {}) => {
  const normalizedUserId = normalizeString(userId);
  const normalizedEmail = normalizeString(email).toLowerCase();

  if (normalizedUserId && USER_LOOKUP_CACHE.has(`userId:${normalizedUserId}`)) {
    return USER_LOOKUP_CACHE.get(`userId:${normalizedUserId}`);
  }

  if (normalizedEmail && USER_LOOKUP_CACHE.has(`email:${normalizedEmail}`)) {
    return USER_LOOKUP_CACHE.get(`email:${normalizedEmail}`);
  }

  return null;
};

export const getUser = async (input) => {
  try {
    const { email, userId, appId } = normalizeUserArgs(input);
    const accessToken = typeof input === 'object' && input ? input.accessToken : undefined;

    if (!email) {
      return {
        success: false,
        message: 'Email is required to fetch user details.',
      };
    }

    const meta = buildMetaPayload({ userId, appId });

    if (!meta?.userId || !meta?.appId) {
      return {
        success: false,
        message: 'Both userId and appId are required to fetch user details.',
      };
    }

    let response;
    let excludedFields = new Set();
    // A profile lookup may retry once after removing every optional field the
    // server explicitly rejects. Keep this compatibility path to two calls.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const includedOptionalFields = USER_PROFILE_FIELDS.filter(
        (field) => !excludedFields.has(field) && !REQUIRED_USER_PROFILE_FIELDS.has(field),
      );
      try {
        response = await executePrivateGraphQl(
          buildUserProfileQuery(excludedFields),
          { email },
          {
            ...(accessToken
              ? { headers: { Authorization: `Bearer ${accessToken}` } }
              : {}),
            sentryIgnoreGraphQlSchemaMismatchFields: includedOptionalFields.map((field) => `UserType.${field}`),
          },
          meta,
        );
      } catch (error) {
        const missingFields = getMissingUserProfileFields(error);
        const optionalMissingFields = missingFields.filter((field) =>
          USER_PROFILE_FIELDS.includes(field) &&
          !REQUIRED_USER_PROFILE_FIELDS.has(field) &&
          !excludedFields.has(field),
        );
        if (attempt === 0 && optionalMissingFields.length === missingFields.length && missingFields.length > 0) {
          excludedFields = new Set([...excludedFields, ...optionalMissingFields]);
          continue;
        }
        throw error;
      }

      const missingFields = getMissingUserProfileFields(response);
      const optionalMissingFields = missingFields.filter((field) =>
        USER_PROFILE_FIELDS.includes(field) &&
        !REQUIRED_USER_PROFILE_FIELDS.has(field) &&
        !excludedFields.has(field),
      );
      if (optionalMissingFields.length === missingFields.length && missingFields.length > 0) {
        excludedFields = new Set([...excludedFields, ...optionalMissingFields]);
        continue;
      }
      break;
    }

    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to fetch user details.',
      };
    }
    const user = cacheUserProfile(response?.data?.data?.user);
    return {
      success: true,
      data: {
        ...response?.data?.data,
        user,
      },
    };
  } catch (error) {
    console.error('Error getting user:', error);
    return { success: false, message: error.message };
  }
};

export const getUserByUserId = async ({ userId, appId, requestUserId, skipCache = false } = {}) => {
  try {
    const resolvedMeta = buildMetaPayload({
      userId: requestUserId,
      appId,
    });
    const targetUserId = normalizeString(userId);

    if (!targetUserId) {
      return {
        success: false,
        message: 'userId is required to fetch user details.',
      };
    }

    if (!skipCache) {
      const cachedUser = readCachedUserProfile({ userId: targetUserId });
      if (cachedUser) {
        return {
          success: true,
          data: {
            userByUserId: cachedUser,
          },
        };
      }
    }

    const query = `
      query GetUserByUserId($userId: ${USER_ID_GRAPHQL_SCALAR}) {
        userByUserId(userId: $userId) {
          userId
          email
          firstName
          lastName
          profileImage
          hourlyRate
          role
          organizationId
          organizationAccessLevel
          accountType
          teamIds
          legalTeamIds
          assignedCases
          notificationCadence
          digestTime
          caseNotificationPrefs
          notificationCaps
          quietHours
          timezone
          firmDigestReport
          phoneNumber
          firmName
          firmAddress
          barNumber
          barAdmissions
          updateChannel
        }
      }
    `;

    if (!resolvedMeta?.userId || !resolvedMeta?.appId) {
      return {
        success: false,
        message: 'Both userId and appId are required to fetch user details.',
      };
    }

    const response = await executePrivateGraphQl(
      query,
      { userId: targetUserId },
      {},
      resolvedMeta
    );
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to fetch user details.',
      };
    }
    const user = cacheUserProfile(response?.data?.data?.userByUserId);

    return {
      success: true,
      data: {
        ...response?.data?.data,
        userByUserId: user,
      },
    };
  } catch (error) {
    console.error('Error getting user by userId:', error);
    return { success: false, message: error.message };
  }
};

export const uploadUserProfilePicture = async ({ userId, fileBase64 } = {}) => {
  try {
    const normalizedUserId = normalizeString(userId);
    const normalizedFileBase64 = normalizeString(fileBase64);

    if (!normalizedUserId) {
      return {
        success: false,
        message: 'userId is required to upload a profile picture.',
      };
    }

    if (!normalizedFileBase64) {
      return {
        success: false,
        message: 'fileBase64 is required to upload a profile picture.',
      };
    }

    const response = await axiosInstance.post('/api/core/user-profile-pic-upload/', {
      userId: normalizedUserId,
      file_base64: normalizedFileBase64,
    });

    return {
      success: true,
      data: response?.data || null,
    };
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error.message ||
        'Unable to upload profile picture.',
    };
  }
};

export const addCaseToUser = async (userId, caseId, appId = null) => {
  try {
    if (!userId || !caseId) {
      return {
        success: false,
        message: 'Both userId and caseId are required to assign a case.',
      };
    }

    // Use the authenticated admin endpoint so the selected target user is
    // carried explicitly in the write payload. The GraphQL mutation can
    // resolve its implicit request user from auth metadata, which is the
    // operator rather than the customer being assigned.
    const response = await axiosInstance.post('/api/core/assign-user-to-case/', {
      caseId: String(caseId),
      userId: String(userId),
      ...(appId ? { appId: String(appId) } : {}),
    });
    const assignment = response?.data || {};

    return {
      success: true,
      data: {
        user: assignment?.user || { userId: String(userId) },
        case: assignment?.case,
      },
    };
  } catch (error) {
    console.error('Error assigning case to user:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export const removeCaseFromUser = async (userId, caseId, appId = null) => {
  try {
    if (!userId || !caseId) {
      return {
        success: false,
        message: 'Both userId and caseId are required to remove a case assignment.',
      };
    }

    const resolvedAppId = appId || resolveGraphQlMeta({ userId }).appId || APP_ID;
    const response = await axiosInstance.post('/api/core/remove-user-from-case/', {
      caseId: String(caseId),
      userId: String(userId),
      appId: String(resolvedAppId),
    });
    const result = response?.data || {};
    if (result.success !== true) {
      return { success: false, message: result.message || result.detail || 'Unable to remove the case assignment.' };
    }
    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error('Error removing case from user:', error);
    return {
      success: false,
      message: error?.response?.data?.detail || error?.response?.data?.message || error.message,
    };
  }
};

export const setFirmCaseArchived = async ({ organizationId, caseId, appId, archived } = {}) => {
  try {
    if (!organizationId || !caseId || !appId || typeof archived !== 'boolean') {
      return { success: false, message: 'Organization, matter, practice area, and archive state are required.' };
    }
    const mutation = `
      mutation SetFirmCaseArchived($organizationId: String!, $caseId: String!, $appId: String!, $archived: Boolean!) {
        setFirmCaseArchived(organizationId: $organizationId, caseId: $caseId, appId: $appId, archived: $archived) {
          success
          archived
          archivedAt
        }
      }
    `;
    const response = await executePrivateGraphQl(mutation, { organizationId, caseId, appId, archived }, { url: privateGraphQlUrl });
    const errors = getGraphQlErrors(response);
    if (Array.isArray(errors) && errors.length) {
      return { success: false, message: errors[0]?.message || 'Unable to update the firm matter list.' };
    }
    const result = response?.data?.data?.setFirmCaseArchived;
    return result?.success === true
      ? { success: true, data: result }
      : { success: false, message: 'Unable to update the firm matter list.' };
  } catch (error) {
    return { success: false, message: error.message || 'Unable to update the firm matter list.' };
  }
};

// Permanently destroys the case row -- the irreversible half of the account
// page's delete dialog, as against `removeCaseFromUser`, which only detaches
// the viewer. The router requires the caller to be the OWNER of the case (the
// owner of its `team_<caseId>` legal team), so a refusal here is expected for a
// colleague merely assigned to the matter.
//
// A GraphQL field error arrives as HTTP 200 with an `errors` array and no
// `data.deleteCase`, so the payload is checked rather than assumed.
export const deleteCasePermanently = async ({ caseId, appId = getCurrentAppId() } = {}) => {
  try {
    if (!caseId || !appId) {
      return {
        success: false,
        message: 'Both caseId and appId are required to delete a case.',
      };
    }

    const idScalar = 'String!';
    const mutation = `
      mutation DeleteCase($caseId: ${idScalar}, $appId: ${idScalar}) {
        deleteCase(caseId: $caseId, appId: $appId) {
          success
        }
      }
    `;

    const response = await executePrivateGraphQl(mutation, { caseId, appId });
    const errors = getGraphQlErrors(response);

    if (Array.isArray(errors) && errors.length) {
      return {
        success: false,
        message: errors[0]?.message || 'Unable to delete this case.',
      };
    }

    const result = response?.data?.data?.deleteCase;
    if (result?.success !== true) {
      return {
        success: false,
        message: 'Unable to delete this case.',
      };
    }

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error('Error deleting the case:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export const getCasesForUser = async ({ email, userId, appId = getCurrentAppId() } = {}) => {
  try {
    if (!email) {
      return {
        success: false,
        message: 'Email is required to fetch cases for a user.',
      };
    }

    const query = `
      query UsersAssignedCases($email: String!) {
        user(email: $email) {
          userCases {
            caseId
            appId
            docketNumber
            caseName
            courtId
            dateFiled
            dateLastFiling
            dateTerminated
            documentNumber
            numberOfDocuments
            numDocketEntries
            summaryS3Url
            practiceArea
            firmArchivedAt
          }
        }
      }
    `;

    const meta = buildMetaPayload({ userId, appId });

    if (!meta?.userId || !meta?.appId) {
      return {
        success: false,
        message: 'Both userId and appId are required to fetch cases for a user.',
      };
    }

    const response = await executePrivateGraphQl(query, { email }, { url: privateGraphQlUrl }, meta);
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to fetch cases for the user.',
      };
    }
    const data = response?.data?.data?.user?.userCases || [];
    const filteredCases = data.filter((item) => sameAppId(item.appId, appId));

    return {
      success: true,
      data: filteredCases,
    };
  } catch (error) {
    console.error('Error getting cases for user:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

// Every case on the user's roster across all products, with the display
// name the Account Options page shows instead of the raw caseId. Unlike
// `getCasesForUser` this does NOT filter by the active appId: the per-matter
// notification settings list `assignedCases`, which spans products.
export const listUserCaseNames = async ({ email, userId, appId } = {}) => {
  try {
    if (!email) {
      return { success: false, message: 'Email is required to list case names.', data: [] };
    }

    const query = `
      query UserCaseNames($email: String!) {
        user(email: $email) {
          userCases {
            caseId
            appId
            caseName
          }
        }
      }
    `;

    const meta = buildMetaPayload({ userId, appId });
    const response = await executePrivateGraphQl(query, { email }, {}, meta);
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to list user case names.',
        data: [],
      };
    }
    const cases = response?.data?.data?.user?.userCases || [];

    return {
      success: true,
      data: cases
        .filter((item) => item?.caseId)
        .map((item) => ({
          caseId: String(item.caseId),
          appId: item.appId == null ? '' : String(item.appId),
          caseName: String(item.caseName || '').trim(),
        })),
    };
  } catch (error) {
    console.error('Error listing user case names:', error);
    return { success: false, message: error.message, data: [] };
  }
};

// Reads the pointers to a user's account-level threads. `accountThreadId` is
// the account manager conversation the dashboard chat dock talks to.
export const getUserThreadPointers = async (input) => {
  try {
    const { email, userId, appId } = normalizeUserArgs(input);

    if (!email) {
      return {
        success: false,
        message: 'Email is required to fetch user thread pointers.',
      };
    }

    const meta = buildMetaPayload({ userId, appId });

    if (!meta?.userId || !meta?.appId) {
      return {
        success: false,
        message: 'Both userId and appId are required to fetch user thread pointers.',
      };
    }

    const query = `
      query UserThreadPointers($email: String!) {
        user(email: $email) {
          userId
          accountThreadId
          contextCrusherThreadId
        }
      }
    `;

    const response = await executePrivateGraphQl(query, { email }, {}, meta);
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to fetch user thread pointers.',
      };
    }

    return {
      success: true,
      data: response?.data?.data?.user || null,
    };
  } catch (error) {
    console.error('Error getting user thread pointers:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export const getOrganizationDetails = async (organizationId) => {
  try {
    const query = `
      query MyQuery($organizationId: ID!) {
        organization(organizationId: $organizationId) {
          organizationId
          organizationName
          workspaceIntent
          setupRole
          agentEnabled
          extendDocumentProcessingEnabled
          jevDataSharingEnabled
          orgUsers {
            userId
            email
            firstName
            lastName
            assignedCases
          }
          firmCases {
            caseId
            appId
            docketNumber
            caseName
            courtId
            dateFiled
            dateLastFiling
            dateTerminated
            documentNumber
            numberOfDocuments
            numDocketEntries
            summaryS3Url
            practiceArea
            firmArchivedAt
          }
        }
      }
    `;

    const response = await executePrivateGraphQl(query, { organizationId }, { url: privateGraphQlUrl });
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return { success: false, message: graphQlErrors[0]?.message || 'Unable to fetch organization details.' };
    }
    const data = response?.data?.data?.organization;
    if (!data) {
      return { success: false, message: 'Organization details were not returned.' };
    }
    try {
      const approvalPolicyResponse = await axiosInstance.get('/api/core/organization-approval-policy/', {
        params: { organizationId },
      });
      data.approvalPolicy = approvalPolicyResponse?.data?.approvalPolicy || {};
    } catch (_error) {
      data.approvalPolicy = {};
    }
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Error getting organization details:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export const setOrganizationAgentEnabled = async (organizationId, agentEnabled) => {
  if (!organizationId || typeof agentEnabled !== 'boolean') {
    return { success: false, message: 'organizationId and agentEnabled are required.' };
  }

  try {
    const mutation = `
      mutation UpdateOrganizationAgent($organizationId: String!, $agentEnabled: Boolean!) {
        updateOrganization(
          organizationId: $organizationId
          organizationData: { organizationId: $organizationId, agentEnabled: $agentEnabled }
        ) {
          organization { organizationId agentEnabled }
        }
      }
    `;
    const response = await executePrivateGraphQl(mutation, { organizationId, agentEnabled }, { url: privateGraphQlUrl });
    const organization = response?.data?.data?.updateOrganization?.organization;
    return organization
      ? { success: true, data: organization }
      : { success: false, message: 'Unable to update firm agent activation.' };
  } catch (error) {
    return { success: false, message: error?.message || 'Unable to update firm agent activation.' };
  }
};

export const setOrganizationJevDataSharingEnabled = async (organizationId, jevDataSharingEnabled) => {
  if (!organizationId || typeof jevDataSharingEnabled !== 'boolean') {
    return { success: false, message: 'organizationId and Jev data-sharing preference are required.' };
  }
  try {
    const mutation = `
      mutation UpdateOrganizationJevDataSharing($organizationId: String!, $enabled: Boolean!) {
        updateOrganization(
          organizationId: $organizationId
          organizationData: { organizationId: $organizationId, jevDataSharingEnabled: $enabled }
        ) { organization { organizationId jevDataSharingEnabled } }
      }
    `;
    const response = await executePrivateGraphQl(mutation, { organizationId, enabled: jevDataSharingEnabled }, { url: privateGraphQlUrl });
    const organization = response?.data?.data?.updateOrganization?.organization;
    return organization
      ? { success: true, data: organization }
      : { success: false, message: 'Unable to update the Jev data-sharing preference.' };
  } catch (error) {
    return { success: false, message: error?.message || 'Unable to update the Jev data-sharing preference.' };
  }
};

export const setOrganizationExtendProcessingEnabled = async (organizationId, extendDocumentProcessingEnabled) => {
  if (!organizationId || typeof extendDocumentProcessingEnabled !== 'boolean') {
    return { success: false, message: 'organizationId and Extend processing preference are required.' };
  }
  try {
    const mutation = `
      mutation UpdateOrganizationExtendProcessing($organizationId: String!, $enabled: Boolean!) {
        updateOrganization(
          organizationId: $organizationId
          organizationData: { organizationId: $organizationId, extendDocumentProcessingEnabled: $enabled }
        ) { organization { organizationId extendDocumentProcessingEnabled } }
      }
    `;
    const response = await executePrivateGraphQl(mutation, { organizationId, enabled: extendDocumentProcessingEnabled }, { url: privateGraphQlUrl });
    const organization = response?.data?.data?.updateOrganization?.organization;
    return organization
      ? { success: true, data: organization }
      : { success: false, message: 'Unable to update the document processing preference.' };
  } catch (error) {
    return { success: false, message: error?.message || 'Unable to update the document processing preference.' };
  }
};

export const setOrganizationApprovalPolicy = async (organizationId, approvalPolicy) => {
  if (!organizationId || !approvalPolicy || typeof approvalPolicy !== 'object') {
    return { success: false, message: 'organizationId and approvalPolicy are required.' };
  }
  try {
    const response = await axiosInstance.patch('/api/core/organization-approval-policy/', { organizationId, approvalPolicy });
    const data = response?.data;
    return data?.organizationId && data?.approvalPolicy
      ? { success: true, data }
      : { success: false, message: 'Unable to update approval policy.' };
  } catch (error) {
    return { success: false, message: error?.message || 'Unable to update approval policy.' };
  }
};

export const orgUsersWithAppAccess = async (organizationId) => {
  try {
    const organizationIdScalar = 'String!';
    const query = `
      query MyQuery($organizationId: ${organizationIdScalar}) {
        orgUsersWithAppAccess(organizationId: $organizationId) {
          accessLevel
          appId
          expirationDate
          grantedDate
          organizationId
          userId
        }
      }
    `;

    const response = await executePrivateGraphQl(query, { organizationId }, { url: privateGraphQlUrl });
    const graphQlErrors = getGraphQlErrors(response);
    if (Array.isArray(graphQlErrors) && graphQlErrors.length > 0) {
      return {
        success: false,
        message: graphQlErrors[0]?.message || 'Unable to fetch organization app access.',
      };
    }
    const data = response?.data?.data?.orgUsersWithAppAccess;
    if (!data) {
      return { success: false, message: 'Organization app access was not returned.' };
    }
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Error getting orgUsersWithAppAccess:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export { normalizeUserArgs };
